import { type BigDecimal, plus } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import type { z } from 'zod'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
  SubTypeRequiredException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { mexcAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

const QUOTE_CURRENCIES = [
  'USDT',
  'USDC',
  'USD1',
  'ETH',
  'BTC',
  'EUR',
  'USDE',
  'BRL',
]

const parseSymbol = (symbol: string): { base: string; quote: string } => {
  for (const quote of QUOTE_CURRENCIES) {
    if (symbol.endsWith(quote)) {
      const base = symbol.slice(0, -quote.length)
      if (base.length > 0) {
        return { base, quote }
      }
    }
  }
  throw new Error(`Unable to parse symbol: ${symbol}`)
}

// MEXC Spot Trade History fetcher
// README の仕様に従い実装
export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  subType,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }
  if (!subType) {
    throw new SubTypeRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  let response: z.infer<typeof responseSchema>
  try {
    response = await client.run({
      method: 'GET',
      path: '/api/v3/myTrades',
      query: {
        symbol: subType,
        ...(from ? { startTime: from.toString() } : {}),
        ...(to ? { endTime: to.toString() } : {}),
        limit: config.limit.toString(),
      },
      responseSchema,
    })
  } catch (error) {
    // Invalid symbol エラーの場合は空の結果を返す（上場廃止されたシンボル等）
    if (error instanceof Error && error.message.includes('Invalid symbol')) {
      return {
        actions: [],
        count: 0,
        from: undefined,
        sources: [],
        stopped: false,
        to: undefined,
      }
    }
    throw error
  }

  // time で降順ソート
  const sorted = response.sort((a, b) => b.time - a.time)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = [] // README 指定: 空配列
  const actions: Action[] = []

  // orderId + time 毎に group
  const groups = new Map<string, typeof sorted>()
  for (const trade of sorted) {
    fetchedFrom =
      fetchedFrom === undefined ? trade.time : Math.min(fetchedFrom, trade.time)
    fetchedTo =
      fetchedTo === undefined ? trade.time : Math.max(fetchedTo, trade.time)
    const key = `${trade.orderId}_${trade.time}`
    const list = groups.get(key) || []
    list.push(trade)
    groups.set(key, list)
  }

  for (const trades of groups.values()) {
    if (trades.length === 0) continue
    const { base: baseSymbol, quote: quoteSymbol } = parseSymbol(subType)

    // base / quote quantity 合算
    let totalBase = trades[0].qty
    let totalQuote = trades[0].quoteQty
    for (let i = 1; i < trades.length; i++) {
      totalBase = plus(totalBase, trades[i].qty)
      totalQuote = plus(totalQuote, trades[i].quoteQty)
    }

    const baseAsset = getAsset(baseSymbol)
    if (!baseAsset) {
      throw new AssetNotFoundException(baseSymbol)
    }
    const quoteAsset = getAsset(quoteSymbol)
    if (!quoteAsset) {
      throw new AssetNotFoundException(quoteSymbol)
    }

    const action: Action = {
      comment: `Trade ID: ${trades.map((t) => t.id).join(' / ')}`,
      evidence: 'system-rule',
      order: 0,
      source: `${mexcAccountCategory}_${config.type}_${trades[0].orderId}_${trades[0].time}`,
      type: 'trade',
      timestamp: trades[0].time,
      transfers: [
        {
          asset: baseAsset,
          amount: totalBase,
          direction: trades[0].isBuyer ? 'in' : 'out',
        },
        {
          asset: quoteAsset,
          amount: totalQuote,
          direction: trades[0].isBuyer ? 'out' : 'in',
        },
      ],
    }

    // commission asset 毎に合算
    const commissionMap = new Map<string, BigDecimal>()
    for (const tr of trades) {
      const existing = commissionMap.get(tr.commissionAsset)
      if (existing) {
        commissionMap.set(tr.commissionAsset, plus(existing, tr.commission))
      } else {
        commissionMap.set(tr.commissionAsset, tr.commission)
      }
    }

    for (const [assetSymbol, totalCommission] of commissionMap.entries()) {
      if (totalCommission.value <= 0n) continue
      const commissionAsset = getAsset(assetSymbol)
      if (!commissionAsset) {
        throw new AssetNotFoundException(assetSymbol)
      }
      action.transfers.push({
        asset: commissionAsset,
        amount: totalCommission,
        direction: 'out',
      })
    }

    actions.push(action)
  }

  // timestamp 降順でソート (既に降順グループ処理しているが明示的に)
  actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions,
    count: response.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
